import { analyticsEvents } from "@healthos/analytics";
import { shareRouteMilestone } from "@healthos/client-data";
import type { api } from "@healthos/schemas";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { invalidateCommunityFeed } from "../api/community-invalidation";
import { client } from "../api/healthos";
import { analytics } from "../lib/analytics";
import { playSound } from "../lib/sound";
import { RouteMilestoneModal } from "./route-milestone-modal";

const MILESTONE_EVENT = "healthos:route-milestone";
/** Resolve the sound after the award reveal. */
const ROUTE_DRAW_MS = 1180;

type MilestoneAnnouncement = {
  celebration: api.RouteMilestoneCelebration;
  /** Dev preview: render every state without publishing anything. */
  preview: boolean;
};

/** Opens the one shared milestone moment from any in-app path. */
export function announceRouteMilestone(
  celebration: api.RouteMilestoneCelebration,
  options: { preview?: boolean } = {},
): void {
  window.dispatchEvent(
    new CustomEvent<MilestoneAnnouncement>(MILESTONE_EVENT, {
      detail: { celebration, preview: options.preview ?? false },
    }),
  );
}

/**
 * A single app-level host, so a milestone cannot open twice when two surfaces
 * notice the same crossing.
 */
export function RouteMilestoneAnnouncer({ viewerFirstName }: { viewerFirstName?: string | null }) {
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState<MilestoneAnnouncement | null>(null);
  const [sharedKey, setSharedKey] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const shareMutation = useMutation({
    mutationFn: (input: api.ShareRouteMilestoneRequest) => shareRouteMilestone(client(), input),
    onSuccess: (_result, input) => {
      analytics.track(analyticsEvents.challengeMilestoneShared, { milestone_kind: input.kind });
      setSharedKey(shareKey(input));
      void invalidateCommunityFeed(queryClient);
    },
  });

  useEffect(() => {
    const receive = (event: Event) => {
      const next = (event as CustomEvent<MilestoneAnnouncement>).detail;
      if (next) {
        setSharedKey(null);
        setAnnouncement(next);
      }
    };
    window.addEventListener(MILESTONE_EVENT, receive);
    return () => window.removeEventListener(MILESTONE_EVENT, receive);
  }, []);

  useEffect(() => {
    if (!announcement || announcement.preview) return;
    analytics.track(analyticsEvents.challengeMilestoneCelebrationShown, {
      milestone_kind: announcement.celebration.kind,
      milestone_subject: announcement.celebration.subject,
    });
  }, [announcement]);

  useEffect(() => {
    if (!announcement) return;
    playSound("sparkle");
    const resolveTimer = window.setTimeout(() => playSound("success"), ROUTE_DRAW_MS);
    return () => window.clearTimeout(resolveTimer);
  }, [announcement]);

  if (!announcement) return null;
  const { celebration, preview } = announcement;
  // A dev preview reports nothing, the same as its share.
  const trackOutcome = (outcome: "view_awards" | "dismissed") => {
    if (preview) return;
    analytics.track(analyticsEvents.challengeMilestoneCelebrationActed, {
      milestone_kind: celebration.kind,
      milestone_subject: celebration.subject,
      outcome,
    });
  };
  const request: api.ShareRouteMilestoneRequest = {
    kind: celebration.kind,
    enrollmentId: celebration.enrollmentId,
    milestoneIndex: celebration.milestoneIndex,
    groupId: celebration.groupId,
  };

  return (
    <RouteMilestoneModal
      open
      celebration={celebration}
      viewerFirstName={viewerFirstName ?? null}
      onOpenChange={(open) => {
        if (open) return;
        trackOutcome("dismissed");
        setAnnouncement(null);
      }}
      onViewAwards={() => {
        trackOutcome("view_awards");
        setAnnouncement(null);
        navigate("/challenges/awards");
      }}
      onShare={() => (preview ? setSharedKey(shareKey(request)) : shareMutation.mutate(request))}
      sharing={!preview && shareMutation.isPending}
      shared={sharedKey === shareKey(request)}
      shareError={!preview && shareMutation.isError}
    />
  );
}

function shareKey(input: api.ShareRouteMilestoneRequest): string {
  return `${input.kind}:${input.enrollmentId}:${input.milestoneIndex}:${input.groupId ?? "self"}`;
}
